import { Injectable, OnDestroy, signal, effect, inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { EllaIssueDto } from '../../../core/api/galaxy-api-client';
import { AuthService } from '../../../core/services/auth.service';
import { AccessService } from '../../../core/services/access.service';
import { getProblemMessage } from '../../../core/api/api-error.utils';
import { EllaApiService } from './ella-api.service';

const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Polls for open Ella pipeline/connection issues so the notification bell in the main layout stays
 * current as an admin navigates around the app. Modeled on AccessService's auth-gated effect() +
 * setInterval pattern. Active count is derived from the GetActive issue list (both endpoints filter
 * on Status == Open with no limit) rather than making a separate GetActiveCount call.
 */
@Injectable({
  providedIn: 'root'
})
export class EllaIssueNotificationService implements OnDestroy {
  private api = inject(EllaApiService);
  private authService = inject(AuthService);
  private accessService = inject(AccessService);
  private messageService = inject(MessageService);

  private readonly _activeCount = signal(0);
  private readonly _issues = signal<EllaIssueDto[]>([]);

  readonly activeCount = this._activeCount.asReadonly();
  readonly issues = this._issues.asReadonly();

  private refreshTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    effect(() => {
      const canSeeIssues = this.authService.isAuthenticated() && this.accessService.isGalaxyAdmin();

      if (canSeeIssues) {
        this.refresh();
        this.startPeriodicRefresh();
      } else {
        this.stopPeriodicRefresh();
        this._activeCount.set(0);
        this._issues.set([]);
      }
    });
  }

  refresh(): void {
    this.api.getEllaIssuesActive().subscribe({
      next: issues => {
        this._issues.set(issues);
        this._activeCount.set(issues.length);
      },
      error: () => { /* transient failure — keep last known count/list */ }
    });
  }

  dismiss(id: number): void {
    this.api.dismissEllaIssue(id).subscribe({
      next: () => this.refresh(),
      error: (err: unknown) => {
        this.messageService.add({ severity: 'error', summary: 'Failed to dismiss issue', detail: getProblemMessage(err) });
      }
    });
  }

  private startPeriodicRefresh(): void {
    this.stopPeriodicRefresh();
    this.refreshTimer = setInterval(() => this.refresh(), REFRESH_INTERVAL_MS);
  }

  private stopPeriodicRefresh(): void {
    if (this.refreshTimer !== null) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.stopPeriodicRefresh();
  }
}
